import fetch from 'cross-fetch';
import { Cache } from '../cache';
import { IApiOptions } from './interfaces';
import { UrlBuilder } from './UrlBuilder';
import { reformatUrlRelativePaths } from '@fnv-core/utils';

/**
 * Class for managing api calls.
 * Currently only deals with json content type.
 */
export class Api {

  /**
   * Get data from the given url.
   * 
   * @param url                     The url to post to, will be prefixed with {API_PREFIX}.
   * @param {IApiOptions} options   Optional options to use.
   * @returns {Promise}
   */
   static async get<T>(url: string, options?: IApiOptions): Promise<T> {
    const opts = options || {};
    const newUrl = UrlBuilder.buildUrl(url, opts);

    const getResponse = async () => {
      const request = this.buildBasicRequest('GET');
      const response = await this.doFetch<T>(newUrl, request);
      return response;
    };

    let response: T;

    if (opts.cache) {
      response = await Cache.getOrSetAsync<T>(`${newUrl}-GET`, getResponse);
    } else {
      response = await getResponse();
    }

    return response;
  }

  /**
   * Post data to the given url.
   * Will be prefixed with {API_PREFIX}.
   * 
   * @param url                     The url to post to, will be prefixed with {API_PREFIX}.
   * @param data                    The data to post.
   * @param {IApiOptions} options   Optional options to use.
   * @returns {Promise}
   */
  static async post<T>(url: string, data?: any, options?: IApiOptions): Promise<T> {
    return await this.doSendDataRequest(url, data, 'POST', options);
  }

  /**
   * Put data to the given url.
   * Will be prefixed with {API_PREFIX}.
   * 
   * @param url                     The url to post to, will be prefixed with {API_PREFIX}.
   * @param data                    The data to put.
   * @param {IApiOptions} options   Optional options to use.
   * @returns {Promise}
   */
  static async put<T>(url: string, data?: any, options?: IApiOptions): Promise<T> {
    return await this.doSendDataRequest(url, data, 'PUT', options);
  }

  private static async doSendDataRequest<T>(url: string, data: any, method: string, options?: IApiOptions) {
    const body = data || {};
    const opts = options || {};

    const newUrl = UrlBuilder.buildUrl(url, opts);
    let request = this.buildBasicRequest(method);
    request.body = JSON.stringify(body);

    const response = await this.doFetch<T>(newUrl, request);
    return response;
  }

  private static async doFetch<T>(url: string, request: RequestInit): Promise<T> {
    url = reformatUrlRelativePaths(url);
    const response = await fetch(url, request);
    if (!response.ok) {
      throw new Error(response.status.toString());
    }

    if (response.status === 204) {
      // No content
      return new Promise((resolve) => resolve(null));
    }

    return await response.json() as T;
  }

  private static buildBasicRequest(type: string): RequestInit {
    return {
      method: type,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  }
}
